import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { getCurrentUserId } from 'common-src/features/auth';
import { getDashboardClients } from 'common-src/models/Client';

import {
  getCareOrchestrationCapacityTasks,
  getCareOrchestrationCapacityTasksLoaded,
} from 'src/features/careOrchestration';
import useCustomSelector from 'src/hooks/useCustomSelector';

import { getStaffDisplayName, getStaffRole } from '../../balanceAssign';
import { getTaskDisplayTitle } from '../../helpers';
import { currentLoad, nextCrunch } from '../../hudSelectors';
import styles from './CapacityChip.module.scss';

const BAND_RING_CLASS = { ok: styles.ringOk, over: styles.ringOver, breach: styles.ringBreach };
const BAND_TEXT_CLASS = { ok: styles.textOk, over: styles.textOver, breach: styles.textBreach };
const BAND_BAR_CLASS = { ok: styles.barOk, over: styles.barOver, breach: styles.barBreach };

const RING_RADIUS = 9;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const pct = (util) => (Number.isFinite(util) ? Math.round(util * 100) : 0);

const UtilisationRing = ({ util, bandKey }) => {
  const filled = Math.min(1, Math.max(0, util));
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" className={styles.ring}>
      <circle
        cx={12}
        cy={12}
        r={RING_RADIUS}
        fill="none"
        strokeWidth={3}
        className={styles.ringTrack}
      />
      <circle
        cx={12}
        cy={12}
        r={RING_RADIUS}
        fill="none"
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * (1 - filled)}
        transform="rotate(-90 12 12)"
        className={BAND_RING_CLASS[bandKey] || styles.ringOk}
      />
    </svg>
  );
};

UtilisationRing.propTypes = {
  util: PropTypes.number,
  bandKey: PropTypes.oneOf(['ok', 'over', 'breach']),
};

const MeterRow = ({ label, load }) => (
  <div className={styles.meterRow}>
    <div className={styles.meterRowHeader}>
      <span className={styles.meterLabel}>{label}</span>
      <span className={styles.meterMeta}>
        {load.committed} / {load.available} min ·{' '}
        <span className={BAND_TEXT_CLASS[load.band] || styles.textOk}>{pct(load.util)}%</span>
      </span>
    </div>
    <div className={styles.barTrack}>
      <div
        className={BAND_BAR_CLASS[load.band] || styles.barOk}
        style={{ width: `${Math.min(100, pct(load.util))}%` }}
      />
    </div>
  </div>
);

MeterRow.propTypes = {
  label: PropTypes.string,
  load: PropTypes.shape({
    committed: PropTypes.number,
    available: PropTypes.number,
    util: PropTypes.number,
    band: PropTypes.oneOf(['ok', 'over', 'breach']),
  }),
};

const CrunchLine = ({ tasks, crunch }) => {
  if (!crunch) return <div className={styles.crunchEmpty}>No deadline crunch this week.</div>;

  const task = (tasks || []).find((t) => `${t.id}` === `${crunch.taskId}`);
  const title = task ? getTaskDisplayTitle(task) : 'A task';
  const when =
    crunch.daysUntilDue < 0
      ? `${Math.abs(crunch.daysUntilDue)}d overdue`
      : crunch.daysUntilDue === 0
        ? 'due today'
        : crunch.daysUntilDue === 1
          ? 'due tomorrow'
          : `due in ${crunch.daysUntilDue} days`;
  const dueDate = crunch.due ? crunch.due.slice(0, 10) : '';

  return (
    <div className={styles.crunchLine}>
      <span className={styles.crunchDot} />
      <div>
        <div className={styles.crunchTitle}>Next deadline crunch</div>
        <div className={styles.crunchText}>
          <span className={styles.crunchTaskTitle}>{title}</span> {when} ({dueDate}) — projected
          past capacity.
        </div>
      </div>
    </div>
  );
};

CrunchLine.propTypes = {
  tasks: PropTypes.arrayOf(PropTypes.object),
  crunch: PropTypes.shape({
    taskId: PropTypes.string,
    due: PropTypes.string,
    daysUntilDue: PropTypes.number,
  }),
};

const CapacityChip = () => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const currentUserId = useCustomSelector(getCurrentUserId);
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const tasks = useCustomSelector(getCareOrchestrationCapacityTasks) || [];
  const capacityTasksLoaded = useCustomSelector(getCareOrchestrationCapacityTasksLoaded);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentUserClient = useMemo(
    () => clients.find((client) => `${client?.id ?? ''}` === `${currentUserId ?? ''}`) || null,
    [clients, currentUserId],
  );
  const ownerName = getStaffDisplayName(currentUserClient);
  const role = getStaffRole(currentUserClient);
  const dailyMinutes = currentUserClient?.dailyCapacityMinutes;
  const weeklyMinutes = currentUserClient?.weeklyCapacityMinutes;

  const today = useMemo(
    () => currentLoad(tasks, 'day', ownerName, role, dailyMinutes, weeklyMinutes),
    [tasks, ownerName, role, dailyMinutes, weeklyMinutes],
  );
  const week = useMemo(
    () => currentLoad(tasks, 'week', ownerName, role, dailyMinutes, weeklyMinutes),
    [tasks, ownerName, role, dailyMinutes, weeklyMinutes],
  );
  const crunch = useMemo(
    () => nextCrunch(tasks, ownerName, role, dailyMinutes),
    [tasks, ownerName, role, dailyMinutes],
  );

  if (!ownerName || !capacityTasksLoaded) return null;

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={`Your capacity: ${pct(today.util)}% of today used`}
      >
        <UtilisationRing util={today.util} bandKey={today.band} />
        <span className={`${styles.triggerPct} ${BAND_TEXT_CLASS[today.band] || styles.textOk}`}>
          {pct(today.util)}%
        </span>
      </button>

      {open && (
        <div className={styles.popover} role="dialog" aria-label="Your capacity">
          <div className={styles.popoverTitle}>Your capacity</div>
          <div className={styles.meterList}>
            <MeterRow label="Today" load={today} />
            <MeterRow label="This week" load={week} />
          </div>
          <div className={styles.crunchWrap}>
            <CrunchLine tasks={tasks} crunch={crunch} />
          </div>
        </div>
      )}
    </div>
  );
};

CapacityChip.propTypes = {};

export default CapacityChip;
